import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import { IgButton, IgDialog, IgDivider } from "@intelligems/ig-theme";
import {
  ExperienceListVariationDto,
  VariationDto,
} from "@intelligems/ig-types";
import { GenericExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { expressionListToPostFix } from "@intelligems/ig-utils";
import { Grid, Step, StepLabel, Stepper } from "@mui/material";
import { useAnalyticsFetching } from "@src/api/analytics/hooks/use-analytics-fetching";
import { useRolloutExperience } from "@src/api/experiences/rollout-experience";
import { getOnsiteEditsFromExperienceDto } from "@src/api/formatters/from-dto/onsite-edits";
import { getOnsiteInjectionsFromExperienceDto } from "@src/api/formatters/from-dto/onsite-injections";
import { RolloutsAudienceWrapper } from "@src/features/sharedExperience/components/rollouts/pages/RolloutsAudienceWrapper";
import { RolloutsGroupSelection } from "@src/features/sharedExperience/components/rollouts/pages/RolloutsGroupSelection";
import { RolloutsModificationConfirm } from "@src/features/sharedExperience/components/rollouts/pages/RolloutsModificationConfirm";
import { RolloutsSummary } from "@src/features/sharedExperience/components/rollouts/pages/RolloutsSummary";
import {
  convertDashboardFilterToAudience,
  getIsAllVisitors,
  getRolloutType,
  hasNativeAndNonNativeMods,
} from "@src/features/sharedExperience/components/rollouts/utils";
import {
  initRolloutAudiences,
  resetAudiences,
  resetRolloutsInfo,
  selectedAudienceRolloutInfo,
  selectedAudienceState,
} from "@src/store/slices/audiences";
import { selectedDashboardFilters } from "@src/store/slices/dashboardFilters";
import {
  getDialogPropDetails,
  isDialogOpen,
  setDialog,
} from "@src/store/slices/global";
import { initOffersFromVariations } from "@src/store/slices/offers";
import { initOnsiteEdits } from "@src/store/slices/onsiteEdits";
import { initOnsiteInjections } from "@src/store/slices/onsiteInjections";
import { initPageTargeting } from "@src/store/slices/pageTargeting";
import {
  initRedirectsFromVariationsState,
  resetRedirects,
} from "@src/store/slices/redirects";

interface RolloutsDialogProps {
  experience: GenericExperienceFe;
  variations: (Omit<ExperienceListVariationDto, "experienceId"> & {
    experienceId?: string;
  })[];
  isExperienceRender: boolean;
}
export const RolloutsDialog = ({
  experience,
  variations,
  isExperienceRender,
}: RolloutsDialogProps) => {
  const dispatch = useDispatch();
  const dialogProps = useSelector(getDialogPropDetails("rollouts"));
  const dashboardFilters = useSelector(selectedDashboardFilters);
  const audienceRollouts = useSelector(selectedAudienceRolloutInfo);
  const audienceState = useSelector(selectedAudienceState);

  const isOpen = useSelector(isDialogOpen("rollouts"));
  const rolloutExperience = useRolloutExperience(isExperienceRender);
  const [activeStep, setActiveStep] = useImmer<number>(0);
  const [isAllVisitors, setIsAllVisitors] = useImmer(
    getIsAllVisitors(
      expressionListToPostFix(
        experience.id === dashboardFilters.experienceId
          ? convertDashboardFilterToAudience(dashboardFilters) || []
          : []
      ),
      dialogProps?.tableTargeting,
      audienceState?.storedAudience ?? experience?.audience
    )
  );
  const isAnalyticsFetching = useAnalyticsFetching();
  const [selectedVariationId, setSelectedVariationId] = useImmer(
    variations.find(
      (variation) => dialogProps?.variationId || variation.isControl
    )?.id || variations[0].id
  );

  const steps = useMemo(() => {
    const baseSteps = ["Select Group", "Audience", "Summary"];
    if (
      isAllVisitors === "all" &&
      hasNativeAndNonNativeMods(experience.testTypes)
    ) {
      baseSteps.splice(2, 0, "Modifications");
    }
    return baseSteps;
  }, [isAllVisitors, experience.testTypes]);

  const [ignoreOtherChanges, setIgnoreOtherChanges] = useImmer<boolean | null>(
    null
  );
  const handleClose = () => {
    dispatch(setDialog({ dialog: "rollouts", open: false, clearProps: true }));
    dispatch(resetRolloutsInfo());
    if (!isExperienceRender) {
      dispatch(resetAudiences());
      dispatch(resetRedirects());
    }
  };

  const handleNextStep = () => {
    setActiveStep((draft) => draft + 1);
  };
  const handlePreviousStep = () => {
    setActiveStep((draft) => draft - 1);
  };

  const onRolloutClick = () => {
    if (selectedVariationId) {
      rolloutExperience.mutate({
        body: {
          experienceId: experience.id!,
          variationId: selectedVariationId,
          rolloutType:
            ignoreOtherChanges === false &&
            hasNativeAndNonNativeMods(experience.testTypes)
              ? "nonNativeModsOnly"
              : getRolloutType(isAllVisitors, experience),
          setThemeLive:
            experience.testTypes.hasTestContentTheme && isAllVisitors === "all",
          updatedAudience: audienceState.audience
            ? {
                ...audienceState.audience,
                audienceType:
                  audienceState.audience?.filters &&
                  audienceState.audience?.filters.length > 0
                    ? "custom"
                    : "common",
              }
            : undefined,
          requiresLink: isAllVisitors === "link",
        },
        pathParams: {
          experienceId: experience.id!,
        },
        queryParams: undefined,
      });
    }
  };

  useEffect(() => {
    if (audienceState.audience && audienceState.audience.filters.length > 0) {
      setIgnoreOtherChanges(null);
    }
  }, [audienceState?.audience?.filters]);

  // useEffect(() => {
  //   if (rolloutExperience.isSuccess) {
  //     handleClose();
  //   }
  // }, [rolloutExperience.isSuccess]);

  // initialize state
  useEffect(() => {
    if (dialogProps?.variationId) {
      setSelectedVariationId(dialogProps.variationId);
    }
    dispatch(
      initRolloutAudiences({
        originalAudience: experience?.audience,
        dashboardFilters:
          experience.id === dashboardFilters.experienceId
            ? convertDashboardFilterToAudience(dashboardFilters)
            : [],
        analyticTableTargeting: dialogProps?.tableTargeting,
      })
    );

    if (!isExperienceRender) {
      dispatch(
        initPageTargeting({
          // @ts-ignore
          pageTargeting: experience?.experiencePageTargeting || [],
        })
      );

      dispatch(
        initOffersFromVariations({
          variations: variations as VariationDto[],
          isInitialize: true,
        })
      );

      dispatch(
        initOnsiteInjections(
          getOnsiteInjectionsFromExperienceDto(variations as VariationDto[])
        )
      );

      dispatch(
        initOnsiteEdits(
          getOnsiteEditsFromExperienceDto(variations as VariationDto[])
        )
      );

      // Redirects Set
      dispatch(
        initRedirectsFromVariationsState({
          variations: variations as VariationDto[],
        })
      );
    }
  }, [dashboardFilters, dialogProps]);

  const rawContent = useMemo(() => {
    return (
      <Grid
        container
        direction={"column"}
        rowSpacing={2}
        textAlign={"start"}
        // maxWidth={"md"}
        sx={{ zIndex: 1400 }}
      >
        <Grid item>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Grid>

        <IgDivider
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "135px",
          }}
        />
        <Grid item mt={2}>
          {activeStep === 0 ? (
            <RolloutsGroupSelection
              experience={experience}
              variations={variations}
              selectedVariationId={selectedVariationId}
              setSelectedVariationId={setSelectedVariationId}
            />
          ) : activeStep === 1 && selectedVariationId ? (
            <RolloutsAudienceWrapper
              isAllVisitors={isAllVisitors}
              setIsAllVisitors={setIsAllVisitors}
              experience={experience}
            />
          ) : activeStep === 2 && steps.length === 4 && selectedVariationId ? (
            <RolloutsModificationConfirm
              experience={experience}
              ignoreOtherChanges={ignoreOtherChanges}
              setIgnoreOtherChanges={setIgnoreOtherChanges}
              selectedVariationId={selectedVariationId}
            />
          ) : (activeStep === 2 || activeStep === 3) && selectedVariationId ? (
            <RolloutsSummary
              selectedVariationId={selectedVariationId}
              variations={variations}
              experience={experience}
              ignoreOtherChanges={ignoreOtherChanges}
              isAllVisitors={isAllVisitors}
            />
          ) : null}
        </Grid>
        <Grid
          item
          container
          direction={"row"}
          columnSpacing={2}
          justifyContent={"flex-end"}
        >
          <Grid item>
            <IgButton
              sx={{ visibility: activeStep === 0 ? "hidden" : "visible" }}
              onClick={handlePreviousStep}
              color={"secondary"}
            >
              Back
            </IgButton>
          </Grid>

          <Grid item>
            <IgButton
              loading={rolloutExperience.isPending}
              disabled={
                rolloutExperience.isPending ||
                (activeStep === 1 &&
                  isAllVisitors === "limited" &&
                  audienceState.audience &&
                  audienceState.audience.filters.length === 0) ||
                isAnalyticsFetching > 0
              }
              onClick={() => {
                if (
                  activeStep === 3 ||
                  (activeStep === 2 && steps.length === 3)
                ) {
                  onRolloutClick();
                } else {
                  handleNextStep();
                }
              }}
              variant={"contained"}
            >
              {activeStep === 3 || (activeStep === 2 && steps.length === 3)
                ? `Roll Out ${experience.status !== "ended" ? "& End " : ""}Test`
                : "Next"}
            </IgButton>
          </Grid>
        </Grid>
      </Grid>
    );
  }, [
    experience,
    variations,
    selectedVariationId,
    activeStep,
    audienceRollouts,
    isAllVisitors,
    ignoreOtherChanges,
    audienceState,
    dialogProps,
    dashboardFilters,
    rolloutExperience.isPending,
    rolloutExperience.isSuccess,
    isAnalyticsFetching,
  ]);
  return (
    <IgDialog
      title={`Roll Out ${experience.status !== "ended" ? "& End " : ""}Test`}
      rawContent={rawContent}
      handleClose={handleClose}
      showDialog={isOpen}
      showCloseIcon
      yesButtonText={"End My Test"}
      dialogHeight={null}
      fullWidth={true}
      maxWidth={"md"}
      noButtonText={"Cancel"}
      hideActions={true}
      loading={rolloutExperience.isPending}
      // padding={"0px 0px 0px 0px"}
      sx={{ zIndex: 1400 }}
    />
  );
};
